// frontend/src/components/SlotCounter.tsx
//
// Animated counter that mimics a slot machine digit roll when the value changes.
// Each digit independently spins to its new value.

import { useEffect, useRef, useState } from 'react';

interface SlotCounterProps {
  value: number;
  className?: string;
}

const DIGIT_HEIGHT = 1.2; // em – height of one digit cell

function SlotDigit({ digit, animate }: { digit: string; animate: boolean }) {
  const [offset, setOffset] = useState(0);
  const prevDigit = useRef(digit);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevDigit.current = digit;
      return;
    }

    if (digit !== prevDigit.current && animate) {
      // Calculate the shortest spin direction (always spin "down" for slot feel)
      const from = parseInt(prevDigit.current, 10);
      const to = parseInt(digit, 10);
      if (!isNaN(from) && !isNaN(to)) {
        // Spin through a full cycle + land on the new digit
        const spinDistance = from <= to ? (10 - to + from) + 10 : (from - to) + 10;
        setOffset(-spinDistance);
        // Force reflow then animate to 0
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setOffset(0);
          });
        });
      }
    }
    prevDigit.current = digit;
  }, [digit, animate]);

  if (digit === ',' || digit === '.') {
    return <span className="inline-block">{digit}</span>;
  }

  const numDigit = parseInt(digit, 10);
  if (isNaN(numDigit)) {
    return <span className="inline-block">{digit}</span>;
  }

  // Build the digit strip: repeat 0-9 three times so we can scroll through them
  const digits = [];
  for (let cycle = 0; cycle < 3; cycle++) {
    for (let d = 0; d <= 9; d++) {
      digits.push(d);
    }
  }

  const baseIndex = 10 + numDigit; // middle cycle, target digit
  const translateY = (baseIndex + offset) * DIGIT_HEIGHT;

  return (
    <span
      className="inline-block overflow-hidden align-bottom"
      style={{ height: `${DIGIT_HEIGHT}em`, width: '0.65em' }}
    >
      <span
        className="inline-flex flex-col items-center"
        style={{
          transform: `translateY(-${translateY}em)`,
          transition: offset === 0 && !isFirstRender.current
            ? 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
            : 'none',
        }}
      >
        {digits.map((d, i) => (
          <span
            key={i}
            className="block text-center"
            style={{ height: `${DIGIT_HEIGHT}em`, lineHeight: `${DIGIT_HEIGHT}em` }}
          >
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}

export function SlotCounter({ value, className = '' }: SlotCounterProps) {
  const formattedValue = value.toLocaleString();
  const [animate, setAnimate] = useState(false);
  const prevValue = useRef(value);

  useEffect(() => {
    if (prevValue.current !== value) {
      setAnimate(true);
      prevValue.current = value;
    }
  }, [value]);

  return (
    <span className={`inline-flex items-baseline tabular-nums ${className}`}>
      {formattedValue.split('').map((char, i) => (
        <SlotDigit key={`${formattedValue.length}-${i}`} digit={char} animate={animate} />
      ))}
    </span>
  );
}
